// @ts-nocheck
// Lightweight click-to-sort for the admin tables.
//
// Each admin tab (Users, Invitations, Purchases, Reviews) renders a
// table where Shlomo wants to click a column header to flip ordering.
// This hook handles the three-state toggle (desc → asc → cleared, back
// to the original incoming order) and the row-comparator wiring.
//
// Usage:
//   const { sortBy, sortIndicator, sortedRows } = useTableSort(rows, {
//     email:    (r) => r.email,
//     created:  (r) => r.created_at,
//     amount:   (r) => r.amount_cents,        // numbers sort numerically
//     role:     (r) => roleRank(r),           // pre-rank pills
//   });
//   ...
//   <th><SortableTH k="email" sortBy={sortBy} sortIndicator={sortIndicator}>מייל</SortableTH></th>
//
// Comparator values can be string | number | null | undefined; null/empty
// values always sort last regardless of direction (so missing data sinks
// to the bottom — usually what an admin wants). Strings use he-IL collation
// so Hebrew names sort the way Shlomo expects.

import { useMemo, useRef, useState } from "react";

export type SortDir = "asc" | "desc";
export type SortState<K extends string> = { key: K; dir: SortDir } | null;

export function useTableSort<R, K extends string>(
  rows: R[],
  comparators: Record<K, (row: R) => unknown>,
  initial: SortState<K> = null,
) {
  const [sort, setSort] = useState<SortState<K>>(initial);

  // Stash the latest comparators in a ref so the consumer can rebuild
  // the object every render (typical with inline arrow functions) without
  // busting the useMemo cache. Only `rows` and `sort` actually drive the
  // sort, so that's all the dependency list needs.
  const compRef = useRef(comparators);
  compRef.current = comparators;

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const get = compRef.current[sort.key];
    if (!get) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      const ea = va === null || va === undefined || va === "";
      const eb = vb === null || vb === undefined || vb === "";
      if (ea && eb) return 0;
      if (ea) return 1; // empty always last
      if (eb) return -1;
      let cmp: number;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), "he", { numeric: true });
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort]);

  // Three-state cycle: idle → desc → asc → idle.
  // Descending first matches "newest / largest first", which is what
  // an admin usually wants for dates and numbers.
  const sortBy = (key: K) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return null;
    });
  };

  const sortIndicator = (key: K): string => {
    if (!sort || sort.key !== key) return " ⇅";
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  return { sort, sortBy, sortIndicator, sortedRows };
}

// Header cell with click-to-sort. Uses the parent <th>'s normal styling
// but adds a pointer cursor, no text selection, and a small arrow that
// fades when the column isn't the active sort key.
export function SortableTH<K extends string>({
  k,
  sortBy,
  sortIndicator,
  title,
  children,
}: {
  k: K;
  sortBy: (k: K) => void;
  sortIndicator: (k: K) => string;
  title?: string;
  children: React.ReactNode;
}) {
  const ind = sortIndicator(k);
  const isActive = ind === " ▲" || ind === " ▼";
  return (
    <th
      onClick={() => sortBy(k)}
      style={{ cursor: "pointer", userSelect: "none" }}
      title={title || "מיון לפי עמודה זו"}
    >
      {children}
      <span
        style={{
          marginInlineStart: 4,
          fontSize: 11,
          opacity: isActive ? 1 : 0.35,
        }}
      >
        {ind}
      </span>
    </th>
  );
}
